import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Commerce international";
const heroImage = "https://net-basis.net/Bilder/international-trade-dach-local-expertise.webp";
const heroAlt = "Un cadre pointe une carte du monde connectée pour le commerce international sur le marché DACH.";
const heroClaimLine1 = "Dépasser les frontières !";
const heroClaimLine2 = "Grâce au savoir-faire local !";
const benefitsTitle = "Comment vous profitez de notre expertise en __commerce international__ :";
const h2Intro = "NetBasis International est le partenaire commercial associé et de confiance de ses clients sur nos marchés. Nous accompagnons nos fabricants partenaires étrangers dans le lancement de nouvelles activités commerciales avec des entreprises locales et des partenaires ou clients sur place.";
const p1Text = "Nous réalisons les analyses de marché et de commerce nécessaires pour vous apporter les connaissances indispensables face à la concurrence. NetBasis International soutient ses fabricants partenaires dans l'estimation du potentiel commercial et de clientèle, l'évaluation de partenariats potentiels ainsi que dans les questions relatives au contrôle qualité, à la gestion, au style de leadership et aux finances.";
const p1Parts = p1Text.split(COMPANY_NAME);
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Détermination des structures de prix les plus efficaces ainsi que des conditions de paiement et de garantie optimales grâce à un accompagnement lors des négociations contractuelles",
  "Validation juridique et conclusion sécurisée de nouveaux projets commerciaux en collaboration avec la direction, les services juridiques ou des cabinets d'avocats locaux",
  "Perspectives précieuses et mises à jour continues sur les pratiques commerciales locales et les réglementations commerciales",
  "Soutien complet pour l'obtention des licences commerciales nécessaires et la conclusion d'accords commerciaux stratégiques",
  "Réponses professionnelles et clarification de toutes les questions relatives aux procédures de commerce international",
  "Traitement des demandes liées aux procédures d'importation ainsi que l'établissement des déclarations en douane et des documents associés",
  "Garantie de délais de livraison fiables et de calendriers stables grâce à un soutien ciblé de la chaîne logistique",
  "Assurance d'une conformité totale des expéditions et des envois au regard de l'ensemble des règles et réglementations nationales et internationales",
];

export default function CommerceInternationalFrPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Commerce International | NetBasis International"
        description="Votre relais compétent pour les structures commerciales mondiales et les relations d’affaires B2B réussies dans le secteur des TIC."
        ogUrl="https://net-basis.net/fr/services/developpement-commercial/commerce-international"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-center pt-[28%] md:pt-[11%] lg:pt-[14%] pointer-events-none">
                <p
                  className="font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center leading-[1.15] md:leading-[1.5] animate-[globusTextSlide_12s_ease-in-out_infinite] max-w-[85%] sm:max-w-[80%] text-[1.65rem] sm:text-3xl md:text-4xl lg:text-5xl"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  <span className="block">{heroClaimLine1}</span>
                  <span className="block">{heroClaimLine2}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 leading-relaxed text-sm md:text-base mb-4">
              {h2Intro}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
            </div>

            {/* Benefits heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}